import { ChangeDetectionStrategy, Component, computed, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ActivatedRoute, RouterModule } from "@angular/router";
import { rxResource } from "@angular/core/rxjs-interop";
import { catchError, of } from "rxjs";
import { ApiService } from "../core/api.service";
import { clarityRecordingUrl } from "../core/clarity.util";
import { DraftDetail } from "../core/models";

const CONTROL_LABELS: Record<string, string> = {
  manual: "Manual",
  cordless: "Cordless",
  motorized: "A-OK Matter motorized",
};

const SHADING_LABELS: Record<string, string> = {
  "semi-blackout": "Semi blackout",
  blackout: "Blackout",
  transparent: "Transparent",
};

@Component({
  selector: "app-draft-detail-page",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterModule],
  templateUrl: "./draft-detail-page.component.html",
  styleUrls: ["./draft-detail-page.component.scss"],
})
export class DraftDetailPageComponent {
  private readonly api = inject(ApiService);
  private readonly ownerId = inject(ActivatedRoute).snapshot.paramMap.get("ownerId") ?? "";

  private readonly resource = rxResource({
    stream: () =>
      this.api.getDraftAdmin(this.ownerId).pipe(
        catchError((err) => {
          console.error("Failed to load draft:", err);
          return of(null);
        }),
      ),
  });

  readonly loading = this.resource.isLoading;
  readonly draft = computed<DraftDetail | null>(() => this.resource.value() ?? null);

  readonly ownerLabel = computed(() => {
    const raw = this.ownerId.replace(/^guest_/, "");
    return raw.length > 14 ? `${raw.slice(0, 8)}…${raw.slice(-4)}` : raw;
  });

  readonly isGuest = computed(() => this.ownerId.startsWith("guest_"));

  readonly recordingUrl = computed(() =>
    clarityRecordingUrl(this.draft()?.claritySessionId),
  );

  money(value: number): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  }

  controlLabel(control?: string): string {
    return CONTROL_LABELS[control ?? ""] ?? control ?? "";
  }

  shadingLabel(opacity?: string): string {
    return SHADING_LABELS[opacity ?? ""] ?? opacity ?? "";
  }
}
